// src/components/ActionItemsList.js
// One list of action items, used on the mandate detail page (every item on a
// mandate) and the Dashboard (my items across mandates). Both read the same
// rows from /api/action-items, so completing or reassigning an item in one
// place shows everywhere. Every mutation invalidates ['action-items'], which
// also refreshes the interaction panel and the firm-wide Dashboard view.
import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import api from '../lib/api'
import { useAuth } from '../context/AuthContext'

function todayMidnight() { const d = new Date(); d.setHours(0, 0, 0, 0); return d }

function isOverdue(dateStr) {
  if (!dateStr) return false
  return new Date(dateStr + 'T00:00:00') < todayMidnight()
}

function shortDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function useStaffUsers() {
  return useQuery({
    queryKey: ['users', 'staff'],
    queryFn: () => api.get('/api/users', { params: { staff: 'true' } }).then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
  }).data ?? []
}

function useMandateOptions(enabled) {
  return useQuery({
    queryKey: ['mandates', 'all'],
    queryFn: () => api.get('/api/mandates').then(r => r.data.data ?? []),
    staleTime: 5 * 60 * 1000,
    enabled,
  }).data ?? []
}

// Add form and row editor share these fields. `fixedMandate` hides the
// mandate picker (the mandate page already knows which mandate it is).
function ItemFields({ value, onChange, users, mandates, fixedMandate }) {
  const set = (k, v) => onChange({ ...value, [k]: v })
  return (
    <>
      <select className="select" value={value.assigned_to} onChange={e => set('assigned_to', e.target.value)}
        style={{ fontSize: 12, maxWidth: 140 }} aria-label="Assignee">
        <option value="">Unassigned</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      {!fixedMandate && (
        <select className="select" value={value.mandate_id} onChange={e => set('mandate_id', e.target.value)}
          style={{ fontSize: 12, maxWidth: 170 }} aria-label="Mandate">
          <option value="">No mandate</option>
          {mandates.map(m => <option key={m.mandate_id} value={m.mandate_id}>{m.mandate_name}</option>)}
        </select>
      )}
      <input type="date" className="input" value={value.due_date} onChange={e => set('due_date', e.target.value)}
        style={{ fontSize: 12, maxWidth: 140 }} aria-label="Due date" />
    </>
  )
}

// defaultVentureId: the venture page's list; new items are tagged to it and
// there is no mandate picker.
export default function ActionItemsList({ filter, showMandate = false, defaultMandateId = null, defaultVentureId = null, emptyText }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const users = useStaffUsers()
  const fixedMandate = !!(defaultMandateId || defaultVentureId)
  const mandates = useMandateOptions(!fixedMandate)

  const [showCompleted, setShowCompleted] = useState(false)
  const blank = { note: '', assigned_to: profile?.id ?? '', mandate_id: defaultMandateId ?? '', due_date: '' }
  const [draft, setDraft] = useState(blank)
  const [editingId, setEditingId] = useState(null)
  const [editDraft, setEditDraft] = useState(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)
  const [err, setErr] = useState(null)

  // Default the assignee to me once my profile has loaded (it arrives async).
  const defaulted = useRef(false)
  useEffect(() => {
    if (defaulted.current || !profile?.id) return
    defaulted.current = true
    setDraft(d => d.assigned_to ? d : { ...d, assigned_to: profile.id })
  }, [profile?.id])

  const key = ['action-items', 'list', filter]
  const { data: items = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.get('/api/action-items', { params: filter }).then(r => r.data.data ?? []),
  })

  const onError = (e) => { setErr(e.response?.data?.error || e.message); setTimeout(() => setErr(null), 4000) }
  const refresh = () => qc.invalidateQueries({ queryKey: ['action-items'] })

  const createMutation = useMutation({
    mutationFn: payload => api.post('/api/action-items', payload).then(r => r.data),
    onSuccess: () => { setDraft({ ...blank, assigned_to: draft.assigned_to }); refresh() },
    onError,
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...patch }) => api.patch(`/api/action-items/${id}`, patch).then(r => r.data),
    onMutate: async ({ id, ...patch }) => {
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData(key)
      qc.setQueryData(key, old => (old ?? []).map(a => a.action_item_id === id ? { ...a, ...patch } : a))
      return { prev }
    },
    onError: (e, _v, ctx) => { if (ctx?.prev) qc.setQueryData(key, ctx.prev); onError(e) },
    onSettled: refresh,
  })

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/api/action-items/${id}`).then(r => r.data),
    onSuccess: () => { setConfirmDeleteId(null); refresh() },
    onError,
  })

  function submit() {
    const note = draft.note.trim()
    if (!note) return
    createMutation.mutate({
      note,
      assigned_to: draft.assigned_to || null,
      mandate_id:  draft.mandate_id  || null,
      venture_id:  defaultVentureId  || null,
      due_date:    draft.due_date    || null,
    })
  }

  function startEdit(a) {
    setEditingId(a.action_item_id)
    setEditDraft({
      note: a.note, assigned_to: a.assigned_to ?? '',
      mandate_id: a.mandate_id ?? '', due_date: a.due_date ?? '',
    })
  }

  function saveEdit(id) {
    const note = editDraft.note.trim()
    if (!note) return
    updateMutation.mutate({
      id, note,
      assigned_to: editDraft.assigned_to || null,
      mandate_id:  editDraft.mandate_id  || null,
      due_date:    editDraft.due_date    || null,
    })
    setEditingId(null)
  }

  const completedCount = items.filter(a => a.completed).length
  const visible = showCompleted ? items : items.filter(a => !a.completed)

  const linkBtn = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 11, color: 'var(--text-muted)',
  }

  return (
    <div>
      {err && (
        <div role="alert" style={{ margin: '0 0 8px', padding: '8px 12px', borderRadius: 6, fontSize: 12,
          background: 'var(--danger-light, #fee2e2)', color: 'var(--danger)' }}>{err}</div>
      )}

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}><div className="spinner" /></div>
      ) : visible.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
          {emptyText ?? 'No open action items.'}
        </div>
      ) : (
        <div>
          {visible.map(a => {
            const overdue = !a.completed && isOverdue(a.due_date)
            if (editingId === a.action_item_id) {
              return (
                <div key={a.action_item_id} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
                  padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <input className="input" value={editDraft.note} autoFocus
                    onChange={e => setEditDraft({ ...editDraft, note: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(a.action_item_id); if (e.key === 'Escape') setEditingId(null) }}
                    style={{ flex: '1 1 200px', fontSize: 13 }} aria-label="Action item" />
                  <ItemFields value={editDraft} onChange={setEditDraft} users={users} mandates={mandates}
                    fixedMandate={fixedMandate} />
                  <button className="btn btn-primary btn-sm" onClick={() => saveEdit(a.action_item_id)}>Save</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)}>Cancel</button>
                </div>
              )
            }
            return (
              <div key={a.action_item_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                <input type="checkbox" checked={!!a.completed}
                  onChange={() => updateMutation.mutate({ id: a.action_item_id, completed: !a.completed })}
                  aria-label={a.completed ? 'Mark not done' : 'Mark done'}
                  style={{ marginTop: 3, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }} />

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, flex: '1 1 160px', minWidth: 0,
                      textDecoration: a.completed ? 'line-through' : 'none',
                      color: a.completed ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                      {a.note}
                    </span>
                    {a.assigned_user?.name && (
                      <span className="badge badge-gray" style={{ fontSize: 10, flexShrink: 0 }}>{a.assigned_user.name}</span>
                    )}
                    {a.due_date && (
                      <span className={`badge ${overdue ? 'badge-red' : 'badge-gray'}`} style={{ fontSize: 10, flexShrink: 0 }}>
                        {overdue ? `Overdue · ${shortDate(a.due_date)}` : shortDate(a.due_date)}
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
                    {showMandate && (a.mandate
                      ? <Link to={`/mandates/${a.mandate.mandate_id}`} style={{ fontSize: 11, color: 'var(--accent)' }}>
                          {a.mandate.mandate_name}
                        </Link>
                      : a.venture
                      ? <Link to={`/ventures/${a.venture.venture_id}`} style={{ fontSize: 11, color: 'var(--accent)' }}>
                          {a.venture.ref}
                        </Link>
                      : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No mandate</span>
                    )}
                    {a.interaction?.subject_title && (
                      <button style={{ ...linkBtn, textDecoration: 'underline', textDecorationColor: 'var(--border)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}
                        onClick={() => navigate(`/interactions?id=${a.interaction.interaction_id}`)}
                        title={a.interaction.subject_title}>
                        From: {a.interaction.subject_title}
                      </button>
                    )}
                    <span style={{ flex: 1 }} />
                    {confirmDeleteId === a.action_item_id ? (
                      <>
                        <span style={{ fontSize: 11, color: 'var(--danger)' }}>Delete this item?</span>
                        <button style={{ ...linkBtn, color: 'var(--danger)', fontWeight: 600 }}
                          onClick={() => deleteMutation.mutate(a.action_item_id)}>Delete</button>
                        <button style={linkBtn} onClick={() => setConfirmDeleteId(null)}>Keep</button>
                      </>
                    ) : (
                      <>
                        <button style={linkBtn} onClick={() => startEdit(a)}>Edit</button>
                        <button style={linkBtn} onClick={() => setConfirmDeleteId(a.action_item_id)}>Delete</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add form */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="input" value={draft.note} placeholder="Add action item…"
          onChange={e => setDraft({ ...draft, note: e.target.value })}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
          style={{ flex: '1 1 200px', fontSize: 13 }} aria-label="New action item" />
        <ItemFields value={draft} onChange={setDraft} users={users} mandates={mandates}
          fixedMandate={fixedMandate} />
        <button className="btn btn-primary btn-sm" onClick={submit}
          disabled={createMutation.isPending || !draft.note.trim()} style={{ flexShrink: 0 }}>
          {createMutation.isPending ? 'Adding…' : '+ Add'}
        </button>
      </div>

      {completedCount > 0 && (
        <button style={{ ...linkBtn, marginTop: 10 }} onClick={() => setShowCompleted(v => !v)}>
          {showCompleted ? 'Hide completed' : `Show completed (${completedCount})`}
        </button>
      )}
    </div>
  )
}
